import { useMemo, useRef } from 'react'

import { FiltersDefinition, FiltersState, IconType } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { AnalyticsReportResultsDocument } from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { filterDefinitionsFromSchema } from 'modules/ai_reports/components/DatasourceTypeRenderer/filters'
import { ColumnFormatHint } from 'modules/ai_reports/components/DatasourceTypeRenderer/utils'

import ReportTableContent from './ReportTableContent'

interface ReportTableAction {
  label: string
  icon: IconType
  onClick: () => void
}

interface ReportTableProps {
  id: string
  reportName: string
  sqlQuery: string
  tableId: string
  actions?: {
    primary?: ReportTableAction
    secondary?: ReportTableAction[]
  }
  columnFormatHints: ColumnFormatHint[]
  onStateChange: React.ComponentProps<typeof OneDataCollection>['onStateChange']
  onUserFacingSqlQueryChange: (sqlQuery: string) => void
  currentFilters: FiltersState<FiltersDefinition<string>> | undefined
}

const INITIAL_PAGE = 1
const INITIAL_PAGE_SIZE = 10

const ReportTable = ({
  id,
  tableId,
  sqlQuery,
  actions,
  columnFormatHints,
  onStateChange,
  onUserFacingSqlQueryChange,
  currentFilters,
}: ReportTableProps) => {
  const { company } = useCurrentSession()
  // Used to fetch the columns.
  const { data: columnsData } = useQuery(AnalyticsReportResultsDocument, {
    variables: {
      query: sqlQuery,
      tableId: tableId,
      companyId: company.id,
      page: INITIAL_PAGE,
      perPage: INITIAL_PAGE_SIZE,
    },
    fetchPolicy: 'network-only',
  })

  const schemaRef = useRef<typeof columnsData>()
  if (!schemaRef.current && columnsData?.iris.sqlResultsConnection.nodes?.[0]?.schema) {
    schemaRef.current = columnsData
  }

  const schema = schemaRef.current?.iris?.sqlResultsConnection?.nodes?.[0]?.schema

  const filterDefinitions = useMemo(() => filterDefinitionsFromSchema(schema ?? []), [schema])

  if (!columnsData) {
    return <Loading />
  }

  return (
    <ReportTableContent
      id={id}
      sqlQuery={sqlQuery}
      tableId={tableId}
      actions={actions}
      columnFormatHints={columnFormatHints}
      onStateChange={onStateChange}
      onUserFacingSqlQueryChange={onUserFacingSqlQueryChange}
      columnsData={columnsData}
      filterDefinitions={filterDefinitions}
      currentFilters={currentFilters}
    />
  )
}

export default ReportTable
export { type ReportTableAction, type ReportTableProps }
